import { QuickExpand } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickExpand';
import { QuickSave } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickSave';
import { HeaderImageSelection } from 'components/detail/edit/EditDetail/EditHeaderDetail/HeaderImageSelection';
import { TextValuePreview } from 'components/detail/edit/TextValuePreview';
import { GradientLine } from 'components/detail/minute/GradientLine';
import { ImageValuesPreview } from 'components/detail/minute/ImageValuesPreview';
import { LargeTextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/LargeTextValue';
import { TextValue as TextValue2 } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { EBorders, EHovers } from 'libs/styles/styling';
import { useContext, useState } from 'react';
import { CtxDetail } from 'session/ctxDetail';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
}

export const EditDetailHeader = (props: Props): JSX.Element => {
    const ctxDetail = useContext(CtxDetail);
    const [title, setTitle] = useState(ctxDetail.updateDetailInput.detailInput.header.title);
    const [description, setDescription] = useState(
        ctxDetail.updateDetailInput.detailInput.header.text
    );
    const isUpdatingIndex = ctxDetail.updatingIndex === -1;

    function updateTitle(val: string) {
        setTitle(val);
        ctxDetail.changeDetailInput(
            {
                ...ctxDetail.updateDetailInput,
                detailInput: {
                    ...ctxDetail.updateDetailInput.detailInput,
                    header: {
                        ...ctxDetail.updateDetailInput.detailInput.header,
                        title: val
                    }
                }
            },
            { triggerHistory: true, saveImmediately: false }
        );
    }

    function updateDescription(val: string) {
        setDescription(val);
        ctxDetail.changeDetailInput(
            {
                ...ctxDetail.updateDetailInput,
                detailInput: {
                    ...ctxDetail.updateDetailInput.detailInput,
                    header: {
                        ...ctxDetail.updateDetailInput.detailInput.header,
                        text: val
                    }
                }
            },
            { triggerHistory: true, saveImmediately: false }
        );
    }

    return (
        <S_MinuteDetailHeader
            className={props.className}
            $isUpdatingIndex={isUpdatingIndex}
            onClick={() => {
                if (!isUpdatingIndex) {
                    ctxDetail.setUpdatingIndex(-1);
                }
            }}
        >
            <S_SidebarSectionHeader
                $isUpdatingIndex={isUpdatingIndex}
                icon={{
                    iconName: 'heading'
                }}
                title={Localization.HEADER}
                highlighted={!!isUpdatingIndex}
            >
                {isUpdatingIndex && <QuickSave />}
                {!isUpdatingIndex && <QuickExpand />}
            </S_SidebarSectionHeader>
            {!isUpdatingIndex && (
                <S_Content $isUpdatingIndex={isUpdatingIndex}>
                    {title && <S_TextValuePreview value={title} />}
                    {description && <S_TextValuePreview value={description} />}
                    {ctxDetail.updateDetailInput?.detailInput?.header?.imageUrl && (
                        <S_ImageValuesPreview
                            images={[ctxDetail.updateDetailInput?.detailInput?.header?.imageUrl]}
                        />
                    )}
                    {!title &&
                        !description &&
                        !ctxDetail.updateDetailInput?.detailInput?.header?.imageUrl && (
                            <S_TextValuePreview value={Localization.NO_HEADER} />
                        )}
                </S_Content>
            )}
            {isUpdatingIndex && (
                <>
                    <SidebarSubsection title={Localization.TITLE} icon={{ iconName: 'font' }}>
                        <SidebarContent>
                            <S_TextValue2 value={title} onChange={updateTitle} textAlign="left" />
                        </SidebarContent>
                    </SidebarSubsection>
                    <SidebarSubsection
                        title={Localization.DESCRIPTION}
                        icon={{ iconName: 'paragraph' }}
                    >
                        <SidebarContent>
                            <S_LargeTextValue
                                value={description}
                                onChange={updateDescription}
                                maxRows={20}
                            />
                        </SidebarContent>
                    </SidebarSubsection>
                    <SidebarSubsection
                        disableBottomBorder
                        title={Localization.IMAGE}
                        icon={{ iconName: 'image' }}
                    >
                        <SidebarContent>
                            <HeaderImageSelection />
                        </SidebarContent>
                    </SidebarSubsection>
                    <S_GradientLine />
                </>
            )}
        </S_MinuteDetailHeader>
    );
};

const S_MinuteDetailHeader = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
    ${(props) => {
        if (props.$isUpdatingIndex) {
            return css`
                border-right: 1px solid #8f3a19;
                border-left: 1px solid #b19052;
            `;
        }
    }}
    ${(props) => {
        if (!props.$isUpdatingIndex) {
            return css`
                &:hover {
                    ${S_SidebarSectionHeader} {
                        box-shadow: ${EHovers.DARK1};
                    }
                    ${S_Content} {
                        box-shadow: ${EHovers.LIGHT4};
                        cursor: pointer;
                    }
                }
            `;
        }
    }}
`;

const S_SidebarSectionHeader = styled(SidebarSectionHeader)<{ $isUpdatingIndex: boolean }>`
    cursor: ${(props) => (props.$isUpdatingIndex ? 'unset' : 'pointer')};
    border-top: ${(props) => (props.$isUpdatingIndex ? 'unset' : '1px solid' + EBorders.DARK)};
`;

const S_Content = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
`;

const S_TextValuePreview = styled(TextValuePreview)``;

const S_ImageValuesPreview = styled(ImageValuesPreview)``;

const S_TextValue2 = styled(TextValue2)``;

const S_LargeTextValue = styled(LargeTextValue)``;

const S_GradientLine = styled(GradientLine)``;
